import { DeliveryService } from '@/services'
import { startOfDay } from 'date-fns'
import { cloneDeep } from 'lodash'
import { PAYMENT_METHOD } from '@/constants'

export default {
  data() {
    return {
      optimizedDeliveryMethodList: [],
      selectedDeliveryMethod: {},
    }
  },
  methods: {
    /**
     * 配送方法最適化リスト初期化
     */
    initOptimizedDeliveryMethodList() {
      this.optimizedDeliveryMethodList = {
        delivery_methods: [],
        bundled_available_flag: true,
      }
    },
    /**
     * 配送方法選択初期化
     */
    initSelectedDeliveryMethod() {
      this.selectedDeliveryMethod = {
        delivery_id: null,
        delivery_service_id: null,
        cooling_off_flag: false,
        delivery_service_name: null,
        bundle_possible_flag: false,
        bundled_flag: false,
        is_available_delivery_time: false,
        is_payment_show: false,
        cost_delivery_fee: 0,
        payment: [],
        payment_id: null,
        payment_service_id: null,
        desired_delivery_day: null,
        delivery_min_date: null,
        delivery_times: [],
        desired_delivery_time_zone: null,
      }
    },
    /**
     * 配送方法選択
     * @param deliveryId 配送方法ID
     */
    async selectDeliveryFeeOptimization(deliveryId, paymentId, postalCode, prefId) {
      if (!deliveryId) {
        deliveryId = this.optimizedDeliveryMethodList.delivery_methods?.[0]?.delivery_id
      }
      const deliveryMethod = this.optimizedDeliveryMethodList.delivery_methods?.find(
        (item) => item.delivery_id === deliveryId
      )

      // 配送情報
      this.selectedDeliveryMethod.delivery_id = deliveryMethod.delivery_id
      this.selectedDeliveryMethod.delivery_service_id = deliveryMethod?.delivery_service_id
      this.selectedDeliveryMethod.delivery_division_id = deliveryMethod?.delivery_division_id
      this.selectedDeliveryMethod.delivery_division_name = deliveryMethod?.delivery_name
      this.selectedDeliveryMethod.cooling_off_flag = deliveryMethod?.cooling_off_flag

      // 配送時間帯指定可能地域
      this.selectedDeliveryMethod.is_available_delivery_time = deliveryMethod?.available_time_zone_flag

      // 配送料金
      this.selectedDeliveryMethod.flat_rate_delivery_fee = deliveryMethod?.flat_rate_delivery_fee

      // 支払方法
      this.selectedDeliveryMethod.payment = deliveryMethod?.payment
      this.selectedDeliveryMethod.is_payment_show = this.selectedDeliveryMethod.payment?.length > 0
      if (!deliveryMethod?.payment.find((item) => item.payment_id === paymentId)) {
        // 配送業者に紐づく支払方法がない場合は、支払方法を優先順で設定
        const credit = deliveryMethod?.payment.find((item) => item.payment_service_id === PAYMENT_METHOD.CREDIT_CARD)
        const np = deliveryMethod?.payment.find((item) => item.payment_service_id === PAYMENT_METHOD.NP)
        const cashOnDelivery = deliveryMethod?.payment.find(
          (item) => item.payment_service_id === PAYMENT_METHOD.CASH_ON_DELIVERY
        )
        this.selectedDeliveryMethod.payment_id =
          credit?.payment_id || np?.payment_id || cashOnDelivery?.payment_id || deliveryMethod?.payment[0]?.payment_id
        this.selectedDeliveryMethod.payment_service_id =
          credit?.payment_service_id ||
          np?.payment_service_id ||
          cashOnDelivery?.payment_service_id ||
          deliveryMethod?.payment[0]?.payment_service_id
      }

      // 配送料
      this.selectedDeliveryMethod.cost_delivery_fee = deliveryMethod?.cost_delivery_fee

      // 同梱可能配送会社フラグ
      this.selectedDeliveryMethod.bundle_possible_flag = deliveryMethod?.bundle_possible_flag

      // 最短配送予定日
      await this.updateMinDeliveryDate(postalCode, prefId)

      // 配送希望時間帯
      if (this.selectedDeliveryMethod.is_available_delivery_time) {
        await this.fetchDeliveryTime()
      } else {
        this.selectedDeliveryMethod.delivery_times = []
        this.selectedDeliveryMethod.desired_delivery_time_zone = null
      }

      return
    },
    /**
     * 配送費用最適化
     * @param postalCode 郵便番号
     * @param prefId 都道府県ID
     */
    async getDeliveryFeeOptimization(params) {
      this.initOptimizedDeliveryMethodList()
      if (params.postal_code && params.pref_id && params.order_item_list.length > 0) {
        const { data } = await DeliveryService.getDeliveryMethodAutoPostalCode(params)

        // 配送費用最適化処理
        const deliveryMethodList = cloneDeep(
          data.delivery_methods.map((item) => ({
            delivery_id: item?.id,
            delivery_division_id: item?.delivery_division_id,
            delivery_name: item?.delivery_division_name,
            delivery_service_id: item?.delivery_service_id,
            delivery_service_name: item?.service_name,
            cost_delivery_fee: item?.cost_delivery_fee,
            flat_rate_delivery_fee: item?.flat_rate_delivery_fee,
            corporate_delivery_flag: item?.corporate_delivery_flag,
            bundle_possible_flag: item?.bundle_possible_flag,
            cooling_off_flag: item?.cooling_off_flag,
            available_time_zone_flag: item?.available_time_zone_flag,
            sort_no: item?.sort_no,
            optimization_result_flag: item?.optimization_result_flag,
            payment: item?.payment
              ?.map((payment) => ({
                id: payment?.id,
                payment_id: payment?.id,
                payment_service_id: payment?.payment_service_id,
                payment_method: payment?.payment_method,
                rule_max: payment?.rule_max,
                visible: payment?.visible,
                sort_no: payment?.sort_no,
                security_code_optional_flag: payment?.security_code_optional_flag,
              }))
              .sort((a, b) => a?.sort_no - b?.sort_no),
          }))
        )

        // 最安値のフラグが立っている配送業者だけを取得
        const filteredDeliveryMethodList = deliveryMethodList
          .filter((item) => item.optimization_result_flag === true)
          .sort((a, b) => a.sort_no - b.sort_no)
        this.optimizedDeliveryMethodList = {
          delivery_methods: filteredDeliveryMethodList,
          bundled_available_flag: data?.bundled_available_flag,
        }

        // 配送方法選択
        if (
          params?.delivery_id &&
          filteredDeliveryMethodList.find((item) => item.delivery_id === params?.delivery_id)
        ) {
          await this.selectDeliveryFeeOptimization(
            params?.delivery_id,
            params?.payment_id,
            params?.postal_code,
            params?.pref_id
          )
        } else {
          await this.selectDeliveryFeeOptimization(
            filteredDeliveryMethodList[0]?.delivery_id,
            params?.payment_id,
            params?.postal_code,
            params?.pref_id
          )
        }
      }
      return
    },
    /**
     * 最短配送予定日更新
     * @param postalCode 郵便番号
     * @param prefId 都道府県ID
     */
    async updateMinDeliveryDate(postalCode, prefId) {
      if (this.selectedDeliveryMethod.delivery_id && postalCode && prefId) {
        const { data } = await DeliveryService.getDeliveryDateMin({
          id: this.selectedDeliveryMethod.delivery_id,
          pref_id: prefId,
          postal_code: postalCode,
        })
        this.selectedDeliveryMethod.desired_delivery_day = startOfDay(data.delivery_date_start).getTime()
        this.selectedDeliveryMethod.delivery_min_date = data.delivery_date_start
      }
      return
    },
    /**
     * 配送時間帯取得
     */
    async fetchDeliveryTime() {
      if (!this.selectedDeliveryMethod.delivery_id) {
        this.selectedDeliveryMethod.delivery_times = []
        this.selectedDeliveryMethod.desired_delivery_time_zone = null
      } else {
        const { data } = await DeliveryService.getDeliveryTime(this.selectedDeliveryMethod.delivery_id)
        this.selectedDeliveryMethod.delivery_times =
          data.map((item) => ({ value: item.id, label: item.delivery_time, visible: item.visible })) || []
        this.selectedDeliveryMethod.desired_delivery_time_zone = null
      }
      return
    },
  },
}
